import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Mail, FileText, LogOut, Trash2, CheckCircle2, Eye, Sparkles, Plus, Save, Loader2, ExternalLink, Wand2, GitCompare, Upload, Image as ImageIcon, X, BookOpen, ChevronRight, ArrowLeft } from 'lucide-react';
import { marked } from 'marked';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { api, uploadFile } from '../../lib/api';

const TABS = [
  { key: 'messages', label: 'Messages', icon: Mail },
  { key: 'blog', label: 'Blog Posts', icon: FileText },
  { key: 'docs', label: 'Marketing Docs', icon: BookOpen },
];

const AdminDashboardPage = () => {
  const { user, loading, logout } = useAdminAuth();
  const [tab, setTab] = useState('messages');

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-400">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-stone-900 text-white">
        <div className="container mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-rose-300">Akira</div>
            <div className="font-serif text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              Admin Console
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-stone-300 hover:text-white text-sm" target="_blank">View site →</Link>
            <span className="text-stone-400 text-sm hidden sm:inline">{user.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm text-stone-300 hover:text-white"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-6">
          <div className="flex gap-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-colors ${
                    isActive ? 'border-rose-400 text-rose-500' : 'border-transparent text-stone-500 hover:text-stone-800'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <main className="container mx-auto px-6 py-10">
        {tab === 'messages' && <MessagesTab />}
        {tab === 'blog' && <BlogTab />}
        {tab === 'docs' && <MarketingDocsTab />}
      </main>
    </div>
  );
};

// ─── Messages Tab ─────────────────────────────────────────────────────────────
const MessagesTab = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api('/api/admin/messages', { auth: true });
      setMessages(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleRead = async (m) => {
    await api(`/api/admin/messages/${m.id}/read?is_read=${!m.is_read}`, { method: 'PATCH', auth: true });
    setMessages((arr) => arr.map((x) => (x.id === m.id ? { ...x, is_read: !x.is_read } : x)));
    if (selected?.id === m.id) setSelected({ ...selected, is_read: !selected.is_read });
  };

  const remove = async (m) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    await api(`/api/admin/messages/${m.id}`, { method: 'DELETE', auth: true });
    setMessages((arr) => arr.filter((x) => x.id !== m.id));
    if (selected?.id === m.id) setSelected(null);
  };

  if (loading) return <p className="text-stone-400">Loading messages...</p>;
  if (messages.length === 0) {
    return (
      <div className="bg-white border border-stone-100 rounded-2xl p-12 text-center">
        <Mail className="w-10 h-10 text-stone-300 mx-auto mb-4" />
        <p className="text-stone-500">No messages yet. New submissions from the contact form will land here.</p>
      </div>
    );
  }

  const unreadCount = messages.filter((m) => !m.is_read).length;

  return (
    <div>
      <div className="mb-6 flex items-baseline justify-between">
        <div>
          <h2 className="font-serif text-3xl text-stone-900" data-testid="admin-messages-title">
            Customer Messages
          </h2>
          <p className="text-stone-500 text-sm mt-1">
            {messages.length} total · {unreadCount} unread
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        <ul className="md:col-span-2 bg-white border border-stone-100 rounded-2xl overflow-hidden divide-y">
          {messages.map((m) => (
            <li
              key={m.id}
              onClick={() => setSelected(m)}
              data-testid={`admin-message-row-${m.id}`}
              className={`p-4 cursor-pointer transition ${
                selected?.id === m.id ? 'bg-rose-50' : 'hover:bg-stone-50'
              } ${!m.is_read ? 'border-l-4 border-rose-400' : 'border-l-4 border-transparent'}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className={`text-sm ${!m.is_read ? 'font-semibold text-stone-900' : 'text-stone-700'}`}>
                  {m.name}
                </div>
                <div className="text-[10px] text-stone-400">
                  {new Date(m.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="text-xs text-stone-500 truncate">{m.email}</div>
              <div className="text-xs text-stone-600 mt-1 line-clamp-2">{m.message}</div>
              {m.sms_opt_in && (
                <span
                  data-testid={`admin-message-sms-chip-${m.id}`}
                  className="inline-block mt-2 text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 bg-emerald-50 text-emerald-700"
                >
                  SMS Opt-In
                </span>
              )}
            </li>
          ))}
        </ul>

        <div className="md:col-span-3">
          {selected ? (
            <div className="bg-white border border-stone-100 rounded-2xl p-6" data-testid="admin-message-detail">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <div className="font-serif text-2xl text-stone-900">{selected.name}</div>
                  <a href={`mailto:${selected.email}`} className="text-rose-500 text-sm hover:underline">
                    {selected.email}
                  </a>
                  {selected.phone && (
                    <div className="text-stone-500 text-sm">{selected.phone}</div>
                  )}
                  {selected.sms_opt_in && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-2 py-1 bg-emerald-50 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3" />
                      Consented to SMS marketing
                    </div>
                  )}
                  <div className="text-[10px] text-stone-400 mt-1">
                    {new Date(selected.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleRead(selected)}
                    title={selected.is_read ? 'Mark unread' : 'Mark read'}
                    data-testid="admin-message-toggle-read"
                    className="p-2 rounded-lg border border-stone-200 hover:bg-stone-50"
                  >
                    {selected.is_read ? <Eye className="w-4 h-4 text-stone-500" /> : <CheckCircle2 className="w-4 h-4 text-rose-500" />}
                  </button>
                  <button
                    onClick={() => remove(selected)}
                    title="Delete"
                    data-testid="admin-message-delete"
                    className="p-2 rounded-lg border border-stone-200 hover:bg-rose-50 text-rose-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {selected.service_interest && (
                <div className="mb-4 text-xs">
                  <span className="text-stone-500 uppercase tracking-wider mr-2">Interested in:</span>
                  <span className="font-medium text-stone-800">{selected.service_interest}</span>
                </div>
              )}
              <div className="bg-stone-50 rounded-xl p-5 text-stone-800 leading-relaxed whitespace-pre-wrap">
                {selected.message}
              </div>
            </div>
          ) : (
            <div className="bg-white border border-stone-100 rounded-2xl p-12 text-center text-stone-400">
              Select a message to view details.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Blog Tab ─────────────────────────────────────────────────────────────────
const PILLARS = [
  'Precision Weight Management & Metabolic Health',
  'Longevity & Biological Age',
  'Hormones Across the Life Course',
  'Microbiome, Genetics & Multi-Omics',
  "Aesthetics with a Physician's Perspective",
  'Functional & Lifestyle Medicine',
];

// Curated comparison-intent topics — each one targets a high-volume
// "X vs Y" search query that AI Overview / Perplexity / ChatGPT-search loves to cite.
const COMPARISON_TOPICS = [
  {
    title: 'GLP-1 vs Tirzepatide: Which Weight-Loss Medication Is Right for You?',
    angle:
      'Compare Ozempic/Wegovy (semaglutide, GLP-1) with Mounjaro/Zepbound (tirzepatide, GLP-1 + GIP). Cover mechanism differences, expected weight loss (typically 15% vs 20–22%), side-effect profiles, cost, insurance coverage, and which patient profile suits which medication. Address "Can I switch from one to the other?" and "Which one for diabetes vs purely weight loss?"',
    pillar: 'Precision Weight Management & Metabolic Health',
    label: 'GLP-1 vs Tirzepatide',
  },
  {
    title: 'Hormone Replacement Therapy vs Bioidentical Hormones: What\'s the Real Difference?',
    angle:
      'Demystify the term "bioidentical." Compare FDA-approved bioidentical formulations (estradiol patches, micronized progesterone) versus compounded bioidenticals, versus traditional HRT. Address safety evidence, the WHI study reassessment, breast-cancer risk, and what to actually ask for. Include a decision framework for perimenopause vs menopause vs surgical menopause.',
    pillar: 'Hormones Across the Life Course',
    label: 'HRT vs Bioidenticals',
  },
  {
    title: 'IPL vs Thulium Laser for Melasma: Which Treatment Is Safer for Darker Skin Tones?',
    angle:
      'IPL (Intense Pulsed Light) and Thulium (1927 nm fractional laser) both treat pigmentation but have very different safety profiles for Fitzpatrick IV–VI skin. Compare wavelength behavior in pigmented skin, melasma recurrence risk, settings used, and PIH (post-inflammatory hyperpigmentation) risk. Include patient FAQs: "Will this make my melasma worse?", "Is laser safe in summer?"',
    pillar: "Aesthetics with a Physician's Perspective",
    label: 'IPL vs Thulium (Melasma)',
  },
  {
    title: 'Sculptra vs Radiesse: Choosing Between Collagen Biostimulators',
    angle:
      'Both stimulate the body to produce its own collagen but use different mechanisms. Sculptra (poly-L-lactic acid) builds gradually over 3–6 months for global facial rejuvenation; Radiesse (calcium hydroxylapatite) is more immediate and structural for jawline/cheek contouring. Compare results timeline, sessions needed, longevity (~2 yrs vs ~1–2 yrs), and ideal candidates. Address "Can I combine them?" and how they pair with HA fillers.',
    pillar: "Aesthetics with a Physician's Perspective",
    label: 'Sculptra vs Radiesse',
  },
  {
    title: 'PRP vs PRF: When Each Regenerative Therapy Wins',
    angle:
      'Both are autologous platelet treatments but differ in preparation and clinical behavior. PRP (Platelet-Rich Plasma) releases growth factors immediately and pairs well with microneedling and laser. PRF (Platelet-Rich Fibrin) releases them slowly over days and shines for under-eye hollowing and slower-healing zones. Cover spin protocols, anticoagulant vs no-anticoagulant tubes, longevity of results, and when to choose which.',
    pillar: "Aesthetics with a Physician's Perspective",
    label: 'PRP vs PRF',
  },
  {
    title: 'Dermal Fillers vs Biostimulators: Volume Now vs Collagen Later',
    angle:
      'Hyaluronic-acid fillers (Restylane, Juvéderm) and biostimulators (Sculptra, Radiesse) take different approaches to facial volume. HA gives immediate, reversible volume; biostimulators stimulate slow collagen production for longer-lasting structural change. Compare onset, longevity, reversibility (hyaluronidase vs none), cost-per-area, and when to layer them in a multi-year aesthetic plan.',
    pillar: "Aesthetics with a Physician's Perspective",
    label: 'HA Fillers vs Biostimulators',
  },
  {
    title: 'Continuous Glucose Monitoring vs HbA1c: Which Tells You More About Your Metabolic Health?',
    angle:
      'HbA1c gives a 3-month average; CGM gives real-time, meal-by-meal data. For metabolically healthy adults pursuing longevity, CGM reveals individual glucose responses that HbA1c can\'t. Discuss what each reveals, who benefits from CGM (insulin resistance, weight management, longevity-focused patients), cost, and the limitations of CGM data interpretation without a clinician.',
    pillar: 'Longevity & Biological Age',
    label: 'CGM vs HbA1c',
  },
  {
    title: 'DUTCH Test vs Serum Hormone Panel: Which Test Is Right for You?',
    angle:
      'Compare the DUTCH (Dried Urine Test for Comprehensive Hormones) which shows hormone metabolites and patterns over 24 hours, vs a standard serum hormone panel which gives a point-in-time snapshot. Discuss when each test gives more clinical insight (perimenopause, adrenal patterns, estrogen metabolism), cost differences, and how to interpret discrepant results.',
    pillar: 'Hormones Across the Life Course',
    label: 'DUTCH vs Serum Panel',
  },
];

const BlogTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // post object being edited or 'new'
  const [generating, setGenerating] = useState(false);
  const [genForm, setGenForm] = useState({ title: '', pillar: '', author_name: 'Angela Trias, M.D.', angle: '' });

  const load = async () => {
    setLoading(true);
    try {
      const data = await api('/api/admin/blog', { auth: true });
      setPosts(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    title: '', slug: '', excerpt: '', body_markdown: '',
    author_name: 'Angela Trias, M.D.', author_credentials: '',
    cover_image: '', pillar: '', reading_time_min: 8,
    key_takeaways: [], faqs: [], is_published: false,
  });

  const saveEditing = async () => {
    if (!editing.title.trim()) { alert('Title is required'); return; }
    const payload = {
      ...editing,
      reading_time_min: editing.reading_time_min ? Number(editing.reading_time_min) : null,
    };
    try {
      if (editing.id) {
        await api(`/api/admin/blog/${editing.id}`, { method: 'PATCH', body: payload, auth: true });
      } else {
        await api('/api/admin/blog', { method: 'POST', body: payload, auth: true });
      }
      setEditing(null);
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    await api(`/api/admin/blog/${p.id}`, { method: 'DELETE', auth: true });
    await load();
  };

  const togglePublish = async (p) => {
    await api(`/api/admin/blog/${p.id}`, {
      method: 'PATCH',
      body: { is_published: !p.is_published },
      auth: true,
    });
    await load();
  };

  const [genJobId, setGenJobId] = useState(null);
  const [genStatus, setGenStatus] = useState(null); // null | 'pending' | 'running' | 'done' | 'error'

  // Poll job status every 4 seconds while a generation is in flight.
  useEffect(() => {
    if (!genJobId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const job = await api(`/api/admin/blog/generate/jobs/${genJobId}`, { auth: true });
        if (cancelled) return;
        setGenStatus(job.status);
        if (job.status === 'done') {
          setGenerating(false);
          setGenJobId(null);
          setGenForm({ title: '', pillar: '', author_name: 'Angela Trias, M.D.', angle: '' });
          await load();
          // Open the freshly created draft in the editor
          const fresh = await api(`/api/admin/blog/${job.post_id}`, { auth: true });
          setEditing(fresh);
        } else if (job.status === 'error') {
          setGenerating(false);
          setGenJobId(null);
          alert('Generation failed: ' + (job.error || 'unknown error'));
        }
      } catch (e) {
        // Network blip — keep polling, don't break the loop.
        console.warn('poll error', e);
      }
    };
    const interval = setInterval(tick, 4000);
    tick(); // immediate first check
    return () => { cancelled = true; clearInterval(interval); };
  }, [genJobId]);

  const generate = async (override = null) => {
    const source = override || genForm;
    if (!source.title?.trim()) { alert('Add a title or subject first'); return; }
    const payload = {
      title: source.title,
      pillar: source.pillar || '',
      angle: source.angle || '',
      author_name: source.author_name || 'Angela Trias, M.D.',
    };
    setGenerating(true);
    setGenStatus('pending');
    try {
      // New flow: backend returns 202 + job_id immediately, runs Claude in
      // background, polls until done. Avoids ingress-proxy 60s timeout.
      const { job_id } = await api('/api/admin/blog/generate', {
        method: 'POST',
        body: payload,
        auth: true,
      });
      setGenJobId(job_id);
    } catch (e) {
      setGenerating(false);
      setGenStatus(null);
      alert('Failed to queue generation: ' + e.message);
    }
  };

  const generateComparison = (topic) => {
    if (generating) return;
    if (!window.confirm(
      `Generate a draft for:\n\n"${topic.title}"\n\nThis takes 60–90 seconds. The draft will open in the editor and won't be published until you click Publish.`
    )) return;
    generate({
      title: topic.title,
      angle: topic.angle,
      pillar: topic.pillar,
      author_name: 'Angela Trias, M.D.',
    });
  };

  if (editing) {
    return <BlogEditor post={editing} onChange={setEditing} onSave={saveEditing} onCancel={() => setEditing(null)} />;
  }

  return (
    <div data-testid="admin-blog-list">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl text-stone-900">Blog Posts</h2>
          <p className="text-stone-500 text-sm mt-1">{posts.length} total · {posts.filter((p) => p.is_published).length} published</p>
        </div>
        <button
          onClick={startNew}
          data-testid="admin-blog-new"
          className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-stone-800"
        >
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>

      {/* Comparison Article Library — one-click generation of high-intent "X vs Y" posts */}
      <div
        className="bg-gradient-to-br from-stone-900 to-rose-950 text-white rounded-2xl p-6 mb-6"
        data-testid="admin-blog-comparison-library"
      >
        <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <GitCompare className="w-5 h-5 text-rose-300" />
              <h3 className="font-semibold text-white text-lg">Comparison Article Library</h3>
            </div>
            <p className="text-rose-200/80 text-sm leading-relaxed">
              One-click drafts of high-intent <em>&ldquo;X vs Y&rdquo;</em> articles. These are the queries AI
              Overview, Perplexity, and ChatGPT-search love to cite — and they convert.
            </p>
          </div>
          {generating && (
            <div className="flex items-center gap-2 text-rose-200 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating draft...
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          {COMPARISON_TOPICS.map((topic) => (
            <button
              key={topic.label}
              onClick={() => generateComparison(topic)}
              disabled={generating}
              data-testid={`admin-comparison-${topic.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="group text-left p-4 rounded-xl bg-white/5 hover:bg-rose-400/20 border border-white/10 hover:border-rose-300/50 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-rose-200 text-[10px] tracking-[0.2em] uppercase">
                  {topic.pillar.split(' ').slice(0, 3).join(' ')}
                </div>
                <Wand2 className="w-3.5 h-3.5 text-rose-300 group-hover:scale-110 transition-transform" />
              </div>
              <div className="text-white font-medium text-sm leading-snug mb-2">
                {topic.label}
              </div>
              <div className="text-rose-200/60 text-xs leading-snug line-clamp-2">
                {topic.title}
              </div>
            </button>
          ))}
        </div>

        <p className="text-rose-200/60 text-xs mt-4">
          Each draft opens in the editor unpublished — edit anything you want, then hit Publish.
        </p>
      </div>

      {/* AI Generator (custom topic) */}
      <div className="bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-100 rounded-2xl p-6 mb-10" data-testid="admin-blog-ai-generate">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-rose-500" />
          <h3 className="font-semibold text-stone-900">Generate a draft with Claude Sonnet 4.5</h3>
        </div>
        <p className="text-stone-600 text-sm mb-5">
          Just enter a <strong>subject or working title</strong> &mdash; the AI will write a 1200&ndash;2200 word draft with sections, citations, and FAQs. Drafts come back unpublished so you can edit anything before hitting Publish.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1.5 tracking-wide uppercase">
              Subject / working title <span className="text-rose-500">*</span>
            </label>
            <input
              placeholder='e.g. "How GLP-1 affects muscle mass and what to do about it"'
              value={genForm.title}
              onChange={(e) => setGenForm({ ...genForm, title: e.target.value })}
              data-testid="admin-blog-gen-title"
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:border-rose-400 outline-none"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-700 mb-1.5 tracking-wide uppercase">
                Pillar (helps categorise)
              </label>
              <select
                value={genForm.pillar}
                onChange={(e) => setGenForm({ ...genForm, pillar: e.target.value })}
                data-testid="admin-blog-gen-pillar"
                className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:border-rose-400 outline-none bg-white"
              >
                <option value="">— Choose a pillar —</option>
                {PILLARS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-700 mb-1.5 tracking-wide uppercase">
                Author byline
              </label>
              <input
                placeholder="e.g. Angela Trias, M.D."
                value={genForm.author_name}
                onChange={(e) => setGenForm({ ...genForm, author_name: e.target.value })}
                data-testid="admin-blog-gen-author"
                className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:border-rose-400 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1.5 tracking-wide uppercase">
              Angle / extra context <span className="text-stone-400 normal-case">(optional)</span>
            </label>
            <textarea
              placeholder="Optional: what angle to take, which studies to cite, audience tone, must-cover sections..."
              value={genForm.angle}
              onChange={(e) => setGenForm({ ...genForm, angle: e.target.value })}
              data-testid="admin-blog-gen-angle"
              rows={3}
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:border-rose-400 outline-none resize-y"
            />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <button
            onClick={() => generate()}
            disabled={generating}
            data-testid="admin-blog-gen-submit"
            className="inline-flex items-center gap-2 bg-rose-400 hover:bg-rose-500 disabled:opacity-50 text-white px-6 py-2.5 rounded-full font-semibold transition-colors"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? 'Generating…' : 'Generate draft'}
          </button>
          {generating && (
            <span className="text-sm text-stone-600" data-testid="admin-blog-gen-status">
              {genStatus === 'pending' && 'Queued…'}
              {genStatus === 'running' && 'Writing with Claude Sonnet 4.5 (typically 30–60s)…'}
              {!genStatus && 'Submitting job…'}
            </span>
          )}
        </div>
      </div>

      {/* Posts list */}
      {loading ? (
        <p className="text-stone-400">Loading posts...</p>
      ) : posts.length === 0 ? (
        <div className="bg-white border border-stone-100 rounded-2xl p-12 text-center">
          <FileText className="w-10 h-10 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-500">No posts yet. Generate one above or create from scratch.</p>
        </div>
      ) : (
        <div className="bg-white border border-stone-100 rounded-2xl divide-y">
          {posts.map((p) => (
            <div key={p.id} className="p-5 flex items-center gap-4" data-testid={`admin-blog-row-${p.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="font-semibold text-stone-900 truncate">{p.title}</h3>
                  {p.is_published ? (
                    <span className="text-[10px] tracking-wider uppercase bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">Published</span>
                  ) : (
                    <span className="text-[10px] tracking-wider uppercase bg-stone-100 text-stone-600 px-2 py-0.5 rounded">Draft</span>
                  )}
                </div>
                <div className="text-xs text-stone-500 truncate">
                  {p.author_name} · {p.pillar || 'No pillar'} · /{p.slug}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {p.is_published && (
                  <Link
                    to={`/articles/${p.slug}`}
                    target="_blank"
                    className="p-2 rounded-lg border border-stone-200 hover:bg-stone-50"
                    title="View"
                  >
                    <ExternalLink className="w-4 h-4 text-stone-500" />
                  </Link>
                )}
                <button
                  onClick={() => togglePublish(p)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border ${
                    p.is_published
                      ? 'border-stone-200 text-stone-700 hover:bg-stone-50'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  }`}
                  data-testid={`admin-blog-toggle-publish-${p.slug}`}
                >
                  {p.is_published ? 'Unpublish' : 'Publish'}
                </button>
                <button
                  onClick={() => setEditing(p)}
                  data-testid={`admin-blog-edit-${p.slug}`}
                  className="px-3 py-2 rounded-lg text-xs font-medium bg-stone-900 text-white hover:bg-stone-800"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(p)}
                  className="p-2 rounded-lg border border-stone-200 hover:bg-rose-50 text-rose-500"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Blog Editor ──────────────────────────────────────────────────────────────
const BlogEditor = ({ post, onChange, onSave, onCancel }) => {
  const set = (patch) => onChange({ ...post, ...patch });
  const isNew = !post.id;

  return (
    <div data-testid="admin-blog-editor">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={onCancel} className="text-stone-500 hover:text-stone-800 text-sm mb-2">← Back to posts</button>
          <h2 className="font-serif text-3xl text-stone-900">
            {isNew ? 'New Post' : 'Edit Post'}
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="px-5 py-2.5 rounded-full text-sm border border-stone-200 hover:bg-stone-50"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            data-testid="admin-blog-editor-save"
            className="inline-flex items-center gap-2 bg-rose-400 hover:bg-rose-500 text-white px-6 py-2.5 rounded-full text-sm font-semibold"
          >
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Field label="Title">
            <input
              value={post.title}
              onChange={(e) => set({ title: e.target.value })}
              data-testid="admin-blog-editor-title"
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:border-rose-400 outline-none text-lg"
            />
          </Field>
          <Field label="Slug (URL)">
            <input
              value={post.slug}
              onChange={(e) => set({ slug: e.target.value })}
              placeholder="Leave blank to auto-generate from title"
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:border-rose-400 outline-none"
            />
          </Field>
          <Field label="Excerpt (used as meta description)">
            <textarea
              value={post.excerpt}
              onChange={(e) => set({ excerpt: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:border-rose-400 outline-none"
            />
          </Field>
          <Field label="Body (Markdown)">
            <textarea
              value={post.body_markdown}
              onChange={(e) => set({ body_markdown: e.target.value })}
              rows={25}
              data-testid="admin-blog-editor-body"
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:border-rose-400 outline-none font-mono text-sm"
            />
          </Field>
        </div>

        <div className="space-y-5">
          <div className="bg-white border border-stone-100 rounded-2xl p-5 space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={post.is_published}
                onChange={(e) => set({ is_published: e.target.checked })}
                data-testid="admin-blog-editor-publish"
                className="w-4 h-4 accent-rose-400"
              />
              <span className="text-sm font-medium text-stone-800">Published</span>
            </label>
            <Field label="Pillar">
              <select
                value={post.pillar || ''}
                onChange={(e) => set({ pillar: e.target.value })}
                className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none bg-white"
              >
                <option value="">— None —</option>
                {PILLARS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Author name">
              <input
                value={post.author_name}
                onChange={(e) => set({ author_name: e.target.value })}
                className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none"
              />
            </Field>
            <Field label="Author credentials">
              <input
                value={post.author_credentials || ''}
                onChange={(e) => set({ author_credentials: e.target.value })}
                className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none"
              />
            </Field>
            <Field label="Cover image">
              <CoverImageEditor post={post} onChange={(url) => set({ cover_image: url })} />
            </Field>
            <Field label="Reading time (min)">
              <input
                type="number"
                value={post.reading_time_min || ''}
                onChange={(e) => set({ reading_time_min: e.target.value })}
                className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none"
              />
            </Field>
          </div>

          <ListField
            label="Key takeaways"
            items={post.key_takeaways}
            onChange={(v) => set({ key_takeaways: v })}
            placeholder="A short, citable takeaway"
          />

          <FAQEditor
            faqs={post.faqs}
            onChange={(v) => set({ faqs: v })}
          />
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs tracking-[0.2em] uppercase text-stone-600 mb-2">{label}</label>
    {children}
  </div>
);

// ─── Cover Image Editor ───────────────────────────────────────────────────────
// Three ways to set a cover: upload from disk, generate with Nano Banana from
// post metadata (title/excerpt/pillar), or paste an external URL.
const CoverImageEditor = ({ post, onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const onUploadClick = () => fileRef.current?.click();

  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!f) return;
    setUploading(true);
    try {
      const res = await uploadFile('/api/admin/blog/cover/upload', f, { auth: true });
      onChange(res.cover_image);
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const onGenerate = async () => {
    if (!post.title?.trim()) { alert('Add a title first — the AI uses it to compose the image.'); return; }
    setGenerating(true);
    try {
      const res = await api('/api/admin/blog/cover/generate', {
        method: 'POST',
        auth: true,
        body: {
          title: post.title,
          excerpt: post.excerpt || '',
          pillar: post.pillar || '',
        },
      });
      onChange(res.cover_image);
    } catch (err) {
      alert('Generation failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const hasCover = !!post.cover_image;

  return (
    <div className="space-y-3" data-testid="cover-image-editor">
      {hasCover ? (
        <div className="relative group">
          <img
            src={post.cover_image}
            alt="Cover preview"
            data-testid="cover-image-preview"
            className="w-full h-40 object-cover rounded-lg border border-stone-200"
          />
          <button
            type="button"
            onClick={() => onChange('')}
            data-testid="cover-image-remove"
            className="absolute top-2 right-2 bg-stone-900/80 hover:bg-stone-900 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remove cover"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="w-full h-40 rounded-lg border-2 border-dashed border-stone-200 flex items-center justify-center text-stone-400">
          <ImageIcon className="w-8 h-8" />
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onUploadClick}
          disabled={uploading || generating}
          data-testid="cover-image-upload-btn"
          className="inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 border border-stone-200 hover:border-rose-300 hover:bg-rose-50 disabled:opacity-50 transition-colors rounded-lg"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <button
          type="button"
          onClick={onGenerate}
          disabled={uploading || generating}
          data-testid="cover-image-generate-btn"
          className="inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 bg-gradient-to-br from-rose-400 to-amber-400 hover:from-rose-500 hover:to-amber-500 text-white disabled:opacity-50 transition-colors rounded-lg"
        >
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {generating ? 'Generating…' : 'AI Generate'}
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={onFilePicked}
        data-testid="cover-image-file-input"
      />

      <button
        type="button"
        onClick={() => setShowUrl((s) => !s)}
        className="text-[11px] text-stone-500 hover:text-stone-800 tracking-wide uppercase"
      >
        {showUrl ? 'Hide URL field' : 'Or paste an image URL'}
      </button>
      {showUrl && (
        <input
          value={post.cover_image && post.cover_image.startsWith('data:') ? '' : (post.cover_image || '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://example.com/cover.jpg"
          data-testid="cover-image-url-input"
          className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none text-sm"
        />
      )}
      {generating && (
        <p className="text-xs text-stone-500 leading-relaxed">
          Composing a luxury-spa editorial image from your title, excerpt, and pillar (~10s)…
        </p>
      )}
    </div>
  );
};

const ListField = ({ label, items, onChange, placeholder }) => (
  <div className="bg-white border border-stone-100 rounded-2xl p-5">
    <Field label={label}>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={v}
              onChange={(e) => {
                const next = [...items];
                next[i] = e.target.value;
                onChange(next);
              }}
              className="flex-1 px-3 py-2 border border-stone-200 rounded-lg outline-none text-sm"
            />
            <button
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              className="text-rose-500 hover:text-rose-700 px-2"
            >
              ×
            </button>
          </div>
        ))}
        <button
          onClick={() => onChange([...items, ''])}
          className="text-rose-500 hover:text-rose-600 text-sm font-medium inline-flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
        {placeholder && items.length === 0 && (
          <p className="text-xs text-stone-400 italic mt-1">{placeholder}</p>
        )}
      </div>
    </Field>
  </div>
);

const FAQEditor = ({ faqs, onChange }) => (
  <div className="bg-white border border-stone-100 rounded-2xl p-5">
    <Field label="FAQs">
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <div key={i} className="border border-stone-200 rounded-lg p-3 space-y-2">
            <input
              placeholder="Question"
              value={f.q || ''}
              onChange={(e) => {
                const next = [...faqs];
                next[i] = { ...next[i], q: e.target.value };
                onChange(next);
              }}
              className="w-full px-2 py-1.5 border border-stone-200 rounded text-sm"
            />
            <textarea
              placeholder="Answer"
              value={f.a || ''}
              onChange={(e) => {
                const next = [...faqs];
                next[i] = { ...next[i], a: e.target.value };
                onChange(next);
              }}
              rows={2}
              className="w-full px-2 py-1.5 border border-stone-200 rounded text-sm"
            />
            <button
              onClick={() => onChange(faqs.filter((_, idx) => idx !== i))}
              className="text-rose-500 hover:text-rose-700 text-xs"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          onClick={() => onChange([...faqs, { q: '', a: '' }])}
          className="text-rose-500 hover:text-rose-600 text-sm font-medium inline-flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add FAQ
        </button>
      </div>
    </Field>
  </div>
);

// ─── Marketing Docs Tab ────────────────────────────────────────────────────
// Admin-only viewer for the internal marketing playbooks (GSC + Bing setup,
// citation checklist, PR outreach). Content lives in /app/memory/ and is
// served over /api/admin/marketing-docs/{slug} with auth.
const MarketingDocsTab = () => {
  const [docs, setDocs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api('/api/admin/marketing-docs', { auth: true })
      .then(setDocs)
      .catch(() => setDocs([]));
  }, []);

  const openDoc = async (slug) => {
    setLoadingDoc(true);
    try {
      const d = await api(`/api/admin/marketing-docs/${slug}`, { auth: true });
      setSelected(d);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      alert('Could not load doc: ' + e.message);
    } finally {
      setLoadingDoc(false);
    }
  };

  const copyMarkdown = async () => {
    if (!selected) return;
    try {
      await navigator.clipboard.writeText(selected.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('Could not copy to clipboard. Select the text manually.');
    }
  };

  const downloadMarkdown = () => {
    if (!selected) return;
    const blob = new Blob([selected.content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selected.slug}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (selected) {
    return (
      <div data-testid="marketing-doc-viewer">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setSelected(null)}
            data-testid="marketing-doc-back"
            className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-800 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back to docs
          </button>
          <div className="flex gap-2">
            <button
              onClick={copyMarkdown}
              data-testid="marketing-doc-copy"
              className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-stone-200 hover:border-rose-300 rounded-lg transition-colors"
            >
              {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <FileText className="w-3.5 h-3.5" />}
              {copied ? 'Copied!' : 'Copy Markdown'}
            </button>
            <button
              onClick={downloadMarkdown}
              data-testid="marketing-doc-download"
              className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-stone-200 hover:border-rose-300 rounded-lg transition-colors"
            >
              <Upload className="w-3.5 h-3.5 rotate-180" />
              Download .md
            </button>
          </div>
        </div>
        <h1 className="font-serif text-3xl text-stone-900 mb-2">{selected.title}</h1>
        <p className="text-stone-500 mb-8 text-sm">{selected.description}</p>
        <div
          className="prose prose-stone max-w-none prose-headings:font-serif prose-headings:text-stone-900 prose-h1:text-3xl prose-h2:text-2xl prose-h2:mt-10 prose-h3:text-lg prose-h3:mt-6 prose-a:text-rose-500 hover:prose-a:text-rose-600 prose-strong:text-stone-900 prose-code:bg-stone-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-pre:bg-stone-100 prose-pre:text-stone-800"
          data-testid="marketing-doc-content"
          dangerouslySetInnerHTML={{ __html: marked.parse(selected.content) }}
        />
      </div>
    );
  }

  return (
    <div data-testid="marketing-docs-list">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-px bg-rose-400" />
          <span className="text-rose-500 text-[11px] tracking-[0.4em] uppercase font-light">
            Internal · Admin only
          </span>
        </div>
        <h1 className="font-serif text-3xl text-stone-900 mb-2">Marketing Playbooks</h1>
        <p className="text-stone-500 max-w-2xl">
          Off-site marketing docs kept in the codebase so they're versioned with your app and accessible from anywhere you can log in.
        </p>
      </div>

      {docs === null && (
        <p className="text-stone-400">Loading…</p>
      )}

      {docs && docs.length === 0 && (
        <p className="text-stone-400">No docs available.</p>
      )}

      {docs && docs.length > 0 && (
        <div className="space-y-3">
          {docs.map((d) => (
            <button
              key={d.slug}
              onClick={() => openDoc(d.slug)}
              disabled={loadingDoc}
              data-testid={`marketing-doc-card-${d.slug}`}
              className="w-full text-left bg-white border border-stone-200 hover:border-rose-300 hover:shadow-md p-5 flex items-center gap-4 transition-all group"
            >
              <div className="w-11 h-11 bg-rose-50 flex items-center justify-center rounded-lg group-hover:bg-rose-100 transition-colors">
                <BookOpen className="w-5 h-5 text-rose-500" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-stone-900 mb-1">{d.title}</div>
                <div className="text-stone-500 text-sm">{d.description}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-stone-300 group-hover:text-rose-400 group-hover:translate-x-1 transition-all" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


export default AdminDashboardPage;
